import { Contract } from "types/index";
import { Card, Table } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Box, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract } from "@fortawesome/pro-duotone-svg-icons";
import { Add } from "@mui/icons-material";
import { gql } from "__generated__/gql";
import { useApolloClient, useMutation } from "@apollo/client";
import { ContractLink } from "utils/LinkUtils";
import { useConfirm } from "components/hooks/useConfirm";
import { TableId, useBackendTable } from "components/table";
import { SortDirection } from "__generated__/enums";
import { getCharterTypeColor } from "view/charters/utils/CharterUtils";
import { usePermission } from "imports/Utils";

type ChildContractListProps = {
	contract: Pick<Contract, "id" | "charterType" | "charterSubtype">;
	refetch: () => void;
}

const ChildContractList = ({ contract, refetch }: ChildContractListProps) => {

	const confirm = useConfirm();
	const client = useApolloClient();
	const { isReadOnly, isRTR, isFinanceAdmin } = usePermission();
	const color = getCharterTypeColor(contract.charterType, contract.charterSubtype);

	const theme = useTheme();

	const { tableProps } = useBackendTable({
		id: TableId.ContractChildren,
		onChange: async(pagination, sort, _, callback) => {
			try {
				const result = await client.query({
					query: CHILD_CONTRACTS_QUERY,
					variables: {
						search: {
							parentContractId: contract.id,
						},
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "contractNumber",
								direction: SortDirection.Desc,
							} ],
						},
					},
				});

				callback({
					content: result.data.contracts.content ?? [],
					totalResults: result.data.contracts.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ linkChildContract ] = useMutation(LINK_CHILD_CONTRACT_MUTATION, { onCompleted: refetch });

	const onLinkChildContract = () => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "LINK_CHILD_CONTRACT_PROMPT" }),
			content: <div>
				{intl.formatMessage({ id: "LINK_CHILD_CONTRACT_PROMPT_CONTENT" })}
			</div>,
		}).then(() => {
			linkChildContract({
				variables: {
					parentContractId: contract.id,
				},
			});
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "CHILDREN_CONTRACTS" })}
			disablePadding
			actions={[
				{
					icon: <Add fontSize="small" />,
					onClick: onLinkChildContract,
					text: intl.formatMessage({ id: "ADD_BUTTON" }),
					hidden: isReadOnly || !(isRTR || isFinanceAdmin),
				},
			]}
		>
			<Box sx={{ backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#F0F0F0" }}>
				<div className="contract-children">
					<Table
						{...tableProps}
						columns={[ {
							id: "contractNumber",
							title: "",
							disableResizing: true,
							render: (row: Contract) =>
								<div style={{ alignItems: "center", display: "flex" }}>
									<span style={{ marginRight: 15, marginLeft: 5 }}>
										<FontAwesomeIcon icon={faFileContract} style={{ color: color }} size={"2x"} />
									</span>
									<ContractLink contract={row as Contract} />
								</div>,
						},
						]}
					/>
				</div>
			</Box>
		</Card>
	);
};

const CHILD_CONTRACTS_QUERY = gql(`#graphql
	query childContracts($search: ContractSearch!, $pagination: Pagination!) {
		contracts(search: $search, pagination: $pagination) {
			content {
				id
				contractNumber
			}
			totalElements
		}
	}
`);

const LINK_CHILD_CONTRACT_MUTATION = gql(`#graphql
	mutation linkChildContract($parentContractId: Long!) {
		linkChildContract(parentContractId: $parentContractId) {
			id
		}
	}
`);

export default ChildContractList;
